<template>
    <page-container>
        <page-loading v-if="loading"></page-loading>

        <div v-else-if="error" class="p-8">
            <vx-alert color="danger" :closeable="false">
                {{ error }}
            </vx-alert>
            <vx-button class="mt-4" data-test="go-back-btn" @click="goBack">Go Back</vx-button>
        </div>

        <div v-else>
            <div class="mb-0">
                <a
                    href="/contacts/external-contact-lists"
                    class="no-underline! text-sky-600! text-base font-normal inline-flex items-center"
                >
                    <font-awesome-icon widthAuto class="mr-2" :icon="faArrowLeft"></font-awesome-icon>
                    External Lists
                </a>
            </div>

            <h1 class="text-3xl font-extrabold text-slate-700 leading-9 mb-6 lg:mb-8">
                {{ externalList?.name }}
            </h1>

            <div class="flex gap-12">
                <nav class="hidden lg:block w-32 flex-shrink-0">
                    <div class="sticky top-6">
                        <div class="text-sky-600 font-semibold text-base">Access</div>
                    </div>
                </nav>

                <div class="flex-1 min-w-0">
                    <h2 class="text-3xl font-extrabold text-slate-700 leading-9 mb-6">Access</h2>

                    <external-list-group-access
                        :externalListId="externalListId"
                        v-model:groupFilters="templateConfig.group_filters"
                        v-model:groupCollectionId="templateConfig.group_collection_id"
                        v-model:enableGroupAccess="enableGroupAccess"
                    />

                    <vx-sticky-footer class="mt-2 md:mt-8">
                        <vx-button
                            href="/contacts/external-contact-lists"
                            data-test="cancel-button"
                            class="w-20"
                            size="lg"
                            color="muted-dark"
                        >
                            Cancel
                        </vx-button>
                        <vx-button
                            @click="submit()"
                            data-test="update-btn"
                            class="grow"
                            size="lg"
                            color="primary"
                            :loading="processing"
                        >
                            Update Group Access
                        </vx-button>
                    </vx-sticky-footer>
                </div>
            </div>
        </div>
    </page-container>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue';
import { VxAlert, VxButton, VxStickyFooter } from '@voxie/frontend-components';
import { faArrowLeft, faSquareArrowUpLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import PageContainer from '~/components/general/PageContainer.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import ExternalListGroupAccess from '~/components/external-lists/ExternalListGroupAccess.vue';
import { externalListsClient } from '~/services';
import { useToasted } from '~/composables/useToasted';
import { useFormSubmission } from '~/composables/useFormSubmission';
import type {
    ExternalList,
    ExternalListGroupFilter,
    ExternalListTemplateConfig,
} from '~/services/externalLists/externalListsClient.types';

type EditableTemplateConfig = Omit<ExternalListTemplateConfig, 'id' | 'group_filters'> & {
    id: number | null;
    group_filters: ExternalListGroupFilter[];
};

const props = defineProps<{
    externalListId: number | string;
}>();

const toasted = useToasted();

const externalListId = ref<number>(Number(props.externalListId));
const teamId = Spark.state.currentTeam.id;
const context = { teamId };

const externalList = ref<ExternalList | null>(null);
const loading = ref<boolean>(true);
const error = ref<string | null>(null);

const defaultTemplateConfig = (): EditableTemplateConfig => ({
    id: null,
    group_filters: [],
    group_collection_id: null,
});

const templateConfig = ref<EditableTemplateConfig>(defaultTemplateConfig());
const enableGroupAccess = ref<boolean>(false);
const configExists = computed(() => templateConfig.value.id !== null);

onMounted(async () => {
    try {
        const [listResponse, configResponse] = await Promise.all([
            externalListsClient.getExternalLists(context, {
                per_page: 1,
                query: externalListId.value.toString(),
            }),
            externalListsClient.getExternalListTemplateConfig(context, externalListId.value).catch((err) => {
                if (err.response?.status === 404) {
                    return null;
                }
                throw err;
            }),
        ]);

        const lists = listResponse.data?.data || [];
        externalList.value = lists.find((list) => list.id === externalListId.value) ?? null;

        if (!externalList.value) {
            error.value = 'External list not found';
            return;
        }

        if (configResponse?.data) {
            templateConfig.value = {
                id: configResponse.data.id,
                group_filters: configResponse.data.group_filters || [],
                group_collection_id: configResponse.data.group_collection_id || null,
            };
            enableGroupAccess.value = true;
        }
    } catch {
        error.value = 'Failed to load external list. Please try again.';
        toasted.global.platform_error();
    } finally {
        loading.value = false;
    }
});

const { submit, processing } = useFormSubmission({
    data: templateConfig,
    preserveScroll: true,
    submit: async () => {
        if (!enableGroupAccess.value && configExists.value) {
            await externalListsClient.deleteExternalListTemplateConfig(context, externalListId.value);
            templateConfig.value.id = null;
        }

        if (enableGroupAccess.value) {
            const completeFilters = templateConfig.value.group_filters.filter((filter) => {
                return filter.attribute && filter.operator && filter.values && filter.values.length > 0;
            });

            const payload = {
                group_filters: completeFilters.length > 0 ? completeFilters : null,
                group_collection_id: templateConfig.value.group_collection_id || null,
            };

            const response = await externalListsClient.upsertExternalListTemplateConfig(
                context,
                externalListId.value,
                payload
            );
            templateConfig.value.id = response.data.id;
        }
    },
    notification: () => ({
        message: 'Group Access updated',
        action: {
            icon: faSquareArrowUpLeft,
            label: 'Back to External Lists',
            href: '/contacts/external-contact-lists',
        },
    }),
});

const goBack = (): void => {
    window.location.assign('/contacts/external-contact-lists');
};
</script>
